// «Карта»: каталог сервисов по направлениям и граф зависимостей (SVG), панель сервиса, табличный вид.
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ApiError, api } from '../api.ts'
import { AgentLabel } from '../components/AgentAvatar.tsx'
import { DirectionChip, StatusBadge } from '../components/chips.tsx'
import { Icon } from '../components/Icon.tsx'
import { Badge, Empty, ErrorBox, IconButton, Loading, PageHeader, Segmented, cx, Tabs } from '../components/ui.tsx'
import { docHref, formatChars, groupParts } from '../lib/docs.ts'
import { useAsync } from '../lib/hooks.ts'
import { CHANGE_SOURCE_LABELS, formatDateTime, timeAgo } from '../lib/format.ts'
import { Markdown } from '../lib/markdown.tsx'
import { Link, TaskLink, setSearchParams, useSearchParam } from '../router.tsx'
import { setDirections, useNow, useReconnect } from '../store.ts'
import { loadPref, savePref } from '../theme.ts'
import type { CatalogResponse, Dependency, Direction, DocDetail, ServiceCard, ServiceDetail } from '../types.ts'
import { IntentRow } from './TaskDrawer.tsx'

// ───────── категории связей ─────────

type EdgeCat = 'api' | 'webhook' | 'db' | 'files' | 'sso' | 'cron' | 'other'
// Связи — нейтральные линии; тип различается рисунком штриха, а не цветом (цвет в Orbis — только для смысла).
const EDGE_CATS: { id: EdgeCat; label: string; dash: string | undefined }[] = [
  { id: 'api', label: 'API', dash: undefined },
  { id: 'webhook', label: 'Вебхук', dash: '6 4' },
  { id: 'db', label: 'Прямо в БД', dash: '2 3' },
  { id: 'files', label: 'Файлы, фиды, S3', dash: '10 3 2 3' },
  { id: 'sso', label: 'SSO', dash: '1 5' },
  { id: 'cron', label: 'Крон', dash: '12 4' },
  { id: 'other', label: 'Другое', dash: '4 4' },
]
const CAT_DASH = Object.fromEntries(EDGE_CATS.map((c) => [c.id, c.dash])) as Record<EdgeCat, string | undefined>

function DashSample({ cat, className }: { cat: EdgeCat; className?: string }) {
  return (
    <svg width="22" height="6" viewBox="0 0 22 6" aria-hidden="true" className={cx('shrink-0', className)}>
      <line x1="1" y1="3" x2="21" y2="3" stroke="currentColor" strokeWidth="1.6" strokeDasharray={CAT_DASH[cat]} strokeLinecap="round" />
    </svg>
  )
}

export function edgeCategory(how: string): EdgeCat {
  const h = how.trim().toLowerCase()
  if (/^(api|http|rest|grpc|graphql)/.test(h)) return 'api'
  if (/^(webhook|вебхук|hook)/.test(h)) return 'webhook'
  if (/^(db|бд|база|postgres|sql)/.test(h)) return 'db'
  if (/^(file|файл|feed|фид|rsync|s3|ftp|sftp|csv|xml)/.test(h)) return 'files'
  if (/^sso/.test(h)) return 'sso'
  if (/^(cron|крон)/.test(h)) return 'cron'
  if (/webhook/.test(h)) return 'webhook'
  if (/\bapi\b/.test(h)) return 'api'
  return 'other'
}

// ───────── раскладка ─────────

const LANE_W = 188
const LANE_GAP = 60
const NODE_H = 44
const NODE_GAP = 10
const HEAD_H = 46
const PAD = 16

interface Lane {
  id: string
  name: string
  color: string
  direction: Direction | null
  services: ServiceCard[]
}
interface NodePos {
  x: number
  y: number
  lane: number
}
interface EdgeGeom {
  dep: Dependency
  cat: EdgeCat
  d: string
}

function computeLayout(cat: CatalogResponse) {
  const dirs = [...cat.directions].sort((a, b) => a.sort - b.sort)
  const known = new Set(dirs.map((d) => d.id))
  const lanes: Lane[] = dirs.map((d) => ({ id: d.id, name: d.name, color: d.color, direction: d, services: [] }))
  const orphan: Lane = { id: '_none', name: 'Без направления', color: 'var(--fg-subtle)', direction: null, services: [] }
  for (const s of cat.services) {
    const lane = s.direction_id && known.has(s.direction_id) ? lanes.find((l) => l.id === s.direction_id)! : orphan
    lane.services.push(s)
  }
  if (orphan.services.length) lanes.push(orphan)
  const used = lanes.filter((l) => l.services.length)
  for (const l of used) l.services.sort((a, b) => a.name.localeCompare(b.name, 'ru'))

  const svcIds = new Set(cat.services.map((s) => s.id))
  const deps = cat.dependencies.filter((d) => d.from_service !== d.to_service && svcIds.has(d.from_service) && svcIds.has(d.to_service))
  const neighbors = new Map<string, string[]>()
  for (const d of deps) {
    neighbors.set(d.from_service, [...(neighbors.get(d.from_service) ?? []), d.to_service])
    neighbors.set(d.to_service, [...(neighbors.get(d.to_service) ?? []), d.from_service])
  }

  // барицентрическая сортировка внутри колонок — меньше пересечений
  const laneOf = new Map<string, number>()
  used.forEach((l, i) => l.services.forEach((s) => laneOf.set(s.id, i)))
  for (let pass = 0; pass < 4; pass++) {
    const idx = new Map<string, number>()
    used.forEach((l) => l.services.forEach((s, j) => idx.set(s.id, j)))
    for (const l of used) {
      const score = new Map<string, number>()
      for (const s of l.services) {
        const ns = (neighbors.get(s.id) ?? []).filter((n) => laneOf.get(n) !== laneOf.get(s.id))
        score.set(s.id, ns.length ? ns.reduce((acc, n) => acc + (idx.get(n) ?? 0), 0) / ns.length : (idx.get(s.id) ?? 0))
      }
      l.services.sort((a, b) => score.get(a.id)! - score.get(b.id)! || a.name.localeCompare(b.name, 'ru'))
    }
  }

  const pos = new Map<string, NodePos>()
  used.forEach((l, i) => {
    const x = PAD + i * (LANE_W + LANE_GAP)
    l.services.forEach((s, j) => pos.set(s.id, { x, y: PAD + HEAD_H + j * (NODE_H + NODE_GAP), lane: i }))
  })
  const maxRows = Math.max(1, ...used.map((l) => l.services.length))
  const width = PAD * 2 + used.length * LANE_W + Math.max(0, used.length - 1) * LANE_GAP
  const height = PAD * 2 + HEAD_H + maxRows * (NODE_H + NODE_GAP)

  // порты: концы рёбер распределяются по высоте узла, сортируясь по положению второго конца
  type Port = { edge: number; end: 'from' | 'to'; otherY: number }
  const ports = new Map<string, Port[]>()
  const sideOf = (a: NodePos, b: NodePos): 'L' | 'R' => (b.lane > a.lane || b.lane === a.lane ? 'R' : 'L')
  deps.forEach((d, i) => {
    const a = pos.get(d.from_service)!
    const b = pos.get(d.to_service)!
    const ka = `${d.from_service}:${sideOf(a, b)}`
    const kb = `${d.to_service}:${b.lane === a.lane ? 'R' : sideOf(b, a)}`
    ports.set(ka, [...(ports.get(ka) ?? []), { edge: i, end: 'from', otherY: b.y }])
    ports.set(kb, [...(ports.get(kb) ?? []), { edge: i, end: 'to', otherY: a.y }])
  })
  const portY = new Map<string, number>()
  for (const [key, list] of ports) {
    const nodeId = key.slice(0, key.lastIndexOf(':'))
    const p = pos.get(nodeId)!
    list.sort((u, v) => u.otherY - v.otherY)
    const n = list.length
    list.forEach((port, k) => {
      const y = p.y + 8 + ((NODE_H - 16) * (n === 1 ? 0.5 : k / (n - 1)))
      portY.set(`${port.edge}:${port.end}`, y)
    })
  }

  const edges: EdgeGeom[] = deps.map((dep, i) => {
    const a = pos.get(dep.from_service)!
    const b = pos.get(dep.to_service)!
    const ay = portY.get(`${i}:from`) ?? a.y + NODE_H / 2
    const by = portY.get(`${i}:to`) ?? b.y + NODE_H / 2
    let d: string
    if (a.lane === b.lane) {
      const x = a.x + LANE_W
      const bulge = 18 + Math.min(LANE_GAP - 22, Math.abs(by - ay) * 0.18)
      d = `M${x},${ay} C${x + bulge},${ay} ${x + bulge},${by} ${x + 2},${by}`
    } else if (b.lane > a.lane) {
      const sx = a.x + LANE_W
      const ex = b.x - 2
      const dx = Math.max(24, (ex - sx) * 0.45)
      d = `M${sx},${ay} C${sx + dx},${ay} ${ex - dx},${by} ${ex},${by}`
    } else {
      const sx = a.x
      const ex = b.x + LANE_W + 2
      const dx = Math.max(24, (sx - ex) * 0.45)
      d = `M${sx},${ay} C${sx - dx},${ay} ${ex + dx},${by} ${ex},${by}`
    }
    return { dep, cat: edgeCategory(dep.how), d }
  })

  return { lanes: used, pos, edges, width, height, deps }
}

function closure(start: string, adj: Map<string, string[]>, deep: boolean): Set<string> {
  const seen = new Set<string>()
  const queue = [...(adj.get(start) ?? [])]
  while (queue.length) {
    const n = queue.shift()!
    if (seen.has(n) || n === start) continue
    seen.add(n)
    if (deep) queue.push(...(adj.get(n) ?? []))
  }
  return seen
}

type View = 'graph' | 'table'

export function MapPage() {
  const catalog = useAsync(async (signal) => {
    const c = await api.get<CatalogResponse>('/api/catalog', { signal })
    setDirections(c.directions ?? [])
    return c
  }, [])
  useReconnect(catalog.reload)
  const selected = useSearchParam('service')
  const [view, setView] = useState<View>(() => loadPref<View>('map.view', 'graph'))
  const [hover, setHover] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [deep, setDeep] = useState(false)
  const [hiddenCats, setHiddenCats] = useState<Set<EdgeCat>>(new Set())

  const layout = useMemo(() => (catalog.data ? computeLayout(catalog.data) : null), [catalog.data])
  const svcById = useMemo(() => new Map((catalog.data?.services ?? []).map((s) => [s.id, s])), [catalog.data])

  const adj = useMemo(() => {
    const up = new Map<string, string[]>()
    const down = new Map<string, string[]>()
    for (const d of layout?.deps ?? []) {
      up.set(d.from_service, [...(up.get(d.from_service) ?? []), d.to_service])
      down.set(d.to_service, [...(down.get(d.to_service) ?? []), d.from_service])
    }
    return { up, down }
  }, [layout])

  const focus = hover ?? selected
  const hl = useMemo(() => {
    if (!focus || !svcById.has(focus)) return null
    return { up: closure(focus, adj.up, deep), down: closure(focus, adj.down, deep) }
  }, [focus, adj, deep, svcById])

  const needle = q.trim().toLowerCase()
  const matches = useMemo(() => {
    if (!needle) return null
    return new Set(
      (catalog.data?.services ?? [])
        .filter(
          (s) =>
            s.id.includes(needle) ||
            s.name.toLowerCase().includes(needle) ||
            s.purpose.toLowerCase().includes(needle) ||
            (s.server ?? '').toLowerCase().includes(needle) ||
            s.domains.some((d) => d.toLowerCase().includes(needle)),
        )
        .map((s) => s.id),
    )
  }, [needle, catalog.data])

  const select = (id: string | null) => setSearchParams({ service: id })

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-line px-4 pt-5 pb-3 min-[720px]:px-7 min-[720px]:pt-6">
        <PageHeader
          className="mb-4"
          title="Карта сервисов"
          lede={
            catalog.data ? (
              <span className="tnum">
                {catalog.data.services.length} сервисов · {layout?.deps.length ?? 0} связей
                {/* на телефоне наводить нечем — подсказку прячем, шапка ниже */}
                <span className="max-md:hidden"> · наведи на сервис, чтобы увидеть зависимости</span>
              </span>
            ) : (
              'Сервисы Orbis по направлениям и связи между ними'
            )
          }
          actions={
            <Segmented<View>
              label="Вид"
              value={view}
              onChange={(v) => {
                setView(v)
                savePref('map.view', v)
              }}
              items={[
                { id: 'graph', label: 'Граф' },
                { id: 'table', label: 'Таблица' },
              ]}
            />
          }
        />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="relative w-full min-w-[160px] sm:w-64">
          <Icon name="search" size={14} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-subtle" />
          <input
            type="search"
            className="ctl h-9 pl-8"
            placeholder="Сервис, домен, сервер…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Поиск сервиса"
          />
        </div>
        {view === 'graph' ? (
          <>
            <label className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 text-[12.5px] whitespace-nowrap text-muted select-none hover:text-fg">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={deep}
                onChange={(e) => setDeep(e.target.checked)}
              />
              Вся цепочка
            </label>
            {/* на телефоне типы связей — одной строкой с прокруткой внутри, а не в три строки над графом */}
            <div
              className="scroll-thin flex flex-wrap items-center gap-0.5 text-[12px] max-md:min-w-0 max-md:flex-1 max-md:flex-nowrap max-md:overflow-x-auto"
              role="group"
              aria-label="Типы связей"
            >
              {EDGE_CATS.map((c) => {
                const off = hiddenCats.has(c.id)
                return (
                  <button
                    key={c.id}
                    type="button"
                    aria-pressed={!off}
                    onClick={() =>
                      setHiddenCats((s) => {
                        const n = new Set(s)
                        if (n.has(c.id)) n.delete(c.id)
                        else n.add(c.id)
                        return n
                      })
                    }
                    className={cx(
                      'inline-flex shrink-0 items-center gap-1.5 rounded-ctl px-1.5 py-0.5 whitespace-nowrap hover:bg-surface-2',
                      off ? 'text-subtle line-through opacity-60' : 'text-muted',
                    )}
                  >
                    <DashSample cat={c.id} />
                    {c.label}
                  </button>
                )
              })}
            </div>
            <div className="ml-auto hidden items-center gap-3 text-[12px] text-muted xl:flex">
              <span className="inline-flex items-center gap-1">
                <span className="h-3 w-3 rounded-[3px] border-2 border-accent" aria-hidden="true" /> зависит от
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-3 w-3 rounded-[3px] border-2 border-dashed border-accent" aria-hidden="true" /> от него зависят
              </span>
            </div>
          </>
        ) : null}
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="scroll-thin min-w-0 flex-1 overflow-auto">
          {catalog.error && !catalog.data ? (
            <div className="p-4">
              <ErrorBox error={catalog.error} onRetry={catalog.reload} />
            </div>
          ) : !layout || !catalog.data ? (
            <div className="px-4 min-[720px]:px-7">
              <Loading label="Загружаю каталог…" />
            </div>
          ) : catalog.data.services.length === 0 ? (
            <div className="p-4">
              <Empty icon="graph">Каталог сервисов пуст</Empty>
            </div>
          ) : view === 'graph' ? (
            <div className="px-2 py-2 min-[720px]:px-5">
            <Graph
              layout={layout}
              selected={selected}
              focus={focus}
              hl={hl}
              matches={matches}
              hiddenCats={hiddenCats}
              onHover={setHover}
              onSelect={select}
            />
            </div>
          ) : (
            <ServiceTable services={catalog.data.services} deps={layout.deps} matches={matches} selected={selected} onSelect={select} />
          )}
        </div>
        {selected ? (
          <aside
            className="anim-slide-in fixed inset-0 z-50 flex flex-col border-l border-line bg-surface md:static md:z-auto md:w-[440px] md:shrink-0"
            aria-label="Карточка сервиса"
          >
            <ServicePanel id={selected} onSelect={select} onClose={() => select(null)} />
          </aside>
        ) : null}
      </div>
    </div>
  )
}

function Graph({
  layout,
  selected,
  focus,
  hl,
  matches,
  hiddenCats,
  onHover,
  onSelect,
}: {
  layout: ReturnType<typeof computeLayout>
  selected: string | null
  focus: string | null
  hl: { up: Set<string>; down: Set<string> } | null
  matches: Set<string> | null
  hiddenCats: Set<EdgeCat>
  onHover: (id: string | null) => void
  onSelect: (id: string) => void
}) {
  const edgeState = (e: EdgeGeom): 'hi' | 'dim' | 'off' | 'base' => {
    if (hiddenCats.has(e.cat)) return 'off'
    if (!hl || !focus) {
      if (matches) return matches.has(e.dep.from_service) || matches.has(e.dep.to_service) ? 'base' : 'dim'
      return 'base'
    }
    const f = e.dep.from_service
    const t = e.dep.to_service
    const upEdge = (f === focus || hl.up.has(f)) && hl.up.has(t)
    const downEdge = (t === focus || hl.down.has(t)) && hl.down.has(f)
    return upEdge || downEdge ? 'hi' : 'dim'
  }

  const ordered = layout.edges
    .map((e) => ({ e, s: edgeState(e) }))
    .filter((x) => x.s !== 'off')
    .sort((a, b) => Number(a.s === 'hi') - Number(b.s === 'hi'))

  return (
    <div className="relative" style={{ width: layout.width, height: layout.height }} onMouseLeave={() => onHover(null)}>
      {/* полосы направлений */}
      {layout.lanes.map((l, i) => (
        <div
          key={l.id}
          className="absolute rounded-[12px] border border-line bg-surface-2"
          style={{ left: PAD + i * (LANE_W + LANE_GAP) - 6, top: PAD - 6, width: LANE_W + 12, height: layout.height - PAD * 2 + 12 }}
        >
          <div className="flex h-[42px] items-center gap-2 px-2.5">
            <span className="ct truncate" title={l.direction?.description || l.name}>
              {l.name}
            </span>
            <span className="tnum ml-auto rounded-full bg-surface-3 px-1.5 text-[11px] leading-[18px] font-semibold text-muted">{l.services.length}</span>
          </div>
        </div>
      ))}

      <svg className="pointer-events-none absolute inset-0" width={layout.width} height={layout.height} aria-hidden="true">
        <defs>
          <marker id="oma-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0.5 L7.5,4 L0,7.5 z" fill="var(--orbis-brand)" />
          </marker>
        </defs>
        {ordered.map(({ e, s }) => (
          <path
            key={e.dep.id}
            d={e.d}
            fill="none"
            stroke={s === 'hi' ? 'var(--orbis-brand)' : 'var(--orbis-muted)'}
            strokeWidth={s === 'hi' ? 1.6 : 1}
            strokeDasharray={CAT_DASH[e.cat]}
            strokeOpacity={s === 'hi' ? 0.9 : s === 'dim' ? 0.06 : 0.24}
            markerEnd={s === 'hi' ? 'url(#oma-arrow)' : undefined}
          />
        ))}
      </svg>

      {layout.lanes.map((l) =>
        l.services.map((s) => {
          const p = layout.pos.get(s.id)!
          const isFocus = s.id === focus
          const isSel = s.id === selected
          const isUp = hl?.up.has(s.id) ?? false
          const isDown = hl?.down.has(s.id) ?? false
          const dim = hl ? !isFocus && !isUp && !isDown : matches ? !matches.has(s.id) : false
          return (
            <button
              key={s.id}
              type="button"
              // касание на телефоне не должно оставлять «залипшую» подсветку после закрытия карточки
              onPointerEnter={(e) => {
                if (e.pointerType !== 'touch') onHover(s.id)
              }}
              onFocus={() => onHover(s.id)}
              onBlur={() => onHover(null)}
              onClick={() => onSelect(s.id)}
              aria-pressed={isSel}
              title={s.purpose}
              className={cx(
                'absolute flex flex-col justify-center rounded-card border bg-surface px-2.5 text-left shadow-card transition-[opacity,border-color,box-shadow] duration-100',
                isFocus || isSel
                  ? 'border-accent ring-3 ring-accent/20'
                  : isUp
                    ? 'border-accent/70'
                    : isDown
                      ? 'border-dashed border-accent/70'
                      : 'border-line hover:border-line-strong hover:shadow-pop',
                dim && 'opacity-35',
              )}
              style={{ left: p.x, top: p.y, width: LANE_W, height: NODE_H }}
            >
              <span className="truncate text-[13px] leading-tight font-semibold text-fg">{s.name}</span>
              <span className="mt-0.5 truncate font-mono text-[10.5px] leading-tight text-subtle">{s.domains[0] ?? s.server ?? s.id}</span>
            </button>
          )
        }),
      )}
    </div>
  )
}

function ServiceTable({
  services,
  deps,
  matches,
  selected,
  onSelect,
}: {
  services: ServiceCard[]
  deps: Dependency[]
  matches: Set<string> | null
  selected: string | null
  onSelect: (id: string) => void
}) {
  const [sort, setSort] = useState<'direction' | 'name' | 'up' | 'down'>('direction')
  const counts = useMemo(() => {
    const up = new Map<string, number>()
    const down = new Map<string, number>()
    for (const d of deps) {
      up.set(d.from_service, (up.get(d.from_service) ?? 0) + 1)
      down.set(d.to_service, (down.get(d.to_service) ?? 0) + 1)
    }
    return { up, down }
  }, [deps])
  const rows = services
    .filter((s) => !matches || matches.has(s.id))
    .sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name, 'ru')
      if (sort === 'up') return (counts.up.get(b.id) ?? 0) - (counts.up.get(a.id) ?? 0)
      if (sort === 'down') return (counts.down.get(b.id) ?? 0) - (counts.down.get(a.id) ?? 0)
      return (a.direction_id ?? '~').localeCompare(b.direction_id ?? '~') || a.name.localeCompare(b.name, 'ru')
    })
  const th = (id: typeof sort, label: string) => (
    <th scope="col">
      <button type="button" onClick={() => setSort(id)} className={cx('uppercase hover:text-fg', sort === id && 'text-fg')}>
        {label}
        {sort === id ? ' ↓' : ''}
      </button>
    </th>
  )
  return (
    <div className="px-4 py-4 min-[720px]:px-7">
      <div className="overflow-x-auto rounded-[12px] border border-line bg-surface shadow-card">
        <table className="dt min-w-[1000px]">
          <thead>
            <tr>
              {th('name', 'Сервис')}
              {th('direction', 'Направление')}
              <th scope="col">Назначение</th>
              <th scope="col">Сервер</th>
              <th scope="col">Репозиторий</th>
              <th scope="col">Домены</th>
              {th('up', 'Зависит')}
              {th('down', 'Зависят от него')}
              <th scope="col">Владельцы</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr
                key={s.id}
                onClick={() => onSelect(s.id)}
                className={cx('cursor-pointer align-top hover:bg-surface-2', selected === s.id && 'bg-accent-soft')}
              >
                <td>
                  <button type="button" className="text-left font-semibold hover:underline" onClick={() => onSelect(s.id)}>
                    {s.name}
                  </button>
                  <div className="font-mono text-[10.5px] text-subtle">{s.id}</div>
                </td>
                <td>{s.direction_id ? <DirectionChip id={s.direction_id} /> : '—'}</td>
                <td className="max-w-[320px] text-muted">
                  <span className="line-clamp-2">{s.purpose}</span>
                </td>
                <td className="font-mono text-[12px]">{s.server ?? '—'}</td>
                <td className="font-mono text-[12px]">{s.repo ?? '—'}</td>
                <td className="font-mono text-[12px]">{s.domains.join(', ') || '—'}</td>
                <td className="tnum r">{counts.up.get(s.id) ?? 0}</td>
                <td className="tnum r">{counts.down.get(s.id) ?? 0}</td>
                <td className="text-muted">{s.owners.join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function KV({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-2 py-1 text-[12.5px]">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  )
}

function ListBlock({ title, items, mono }: { title: string; items: string[]; mono?: boolean }) {
  if (!items.length) return null
  return (
    <section>
      <h3 className="ct mb-1.5">{title}</h3>
      <ul className={cx('list-disc space-y-0.5 pl-4 text-[12.5px] break-words', mono && 'font-mono text-[11.5px]')}>
        {items.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
    </section>
  )
}

function DepList({
  title,
  deps,
  field,
  onSelect,
}: {
  title: string
  deps: Dependency[]
  field: 'to_service' | 'from_service'
  onSelect: (id: string) => void
}) {
  if (!deps.length) return null
  return (
    <section>
      <h3 className="ct mb-1.5">
        {title} <span className="tnum font-normal">{deps.length}</span>
      </h3>
      <ul className="flex flex-col gap-1">
        {deps.map((d) => (
          <li key={d.id} className="rounded-ctl border border-line bg-surface px-2 py-1.5 text-[12px]">
            <div className="flex items-center gap-1.5">
              <DashSample cat={edgeCategory(d.how)} className="text-muted" />
              <button type="button" className="max-w-full text-left font-semibold break-words hover:underline" onClick={() => onSelect(d[field])}>
                {d[field]}
              </button>
              <span className="ml-auto min-w-0 truncate font-mono text-[10.5px] text-subtle" title={d.how}>
                {d.how}
              </span>
            </div>
            {d.what ? <div className="mt-0.5 break-words text-muted">{d.what}</div> : null}
          </li>
        ))}
      </ul>
    </section>
  )
}

type PanelTab = 'card' | 'doc'

function ServicePanel({ id, onSelect, onClose }: { id: string; onSelect: (id: string) => void; onClose: () => void }) {
  const now = useNow()
  const r = useAsync((signal) => api.get<ServiceDetail>(`/api/services/${encodeURIComponent(id)}`, { signal }), [id])
  const s = r.data?.service
  const [tab, setTab] = useState<PanelTab>('card')
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start gap-2 bg-surface px-5 pt-4 pb-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h2 className="min-w-0 text-[18px] font-[680] tracking-[-0.01em] [overflow-wrap:anywhere]">{s?.name ?? id}</h2>
            {s?.direction_id ? <DirectionChip id={s.direction_id} /> : null}
          </div>
          <div className="font-mono text-[11px] break-words text-subtle">{id}</div>
        </div>
        <IconButton icon="x" label="Закрыть карточку сервиса" onClick={onClose} />
      </div>
      <Tabs<PanelTab>
        label="Разделы карточки сервиса"
        className="shrink-0 bg-surface px-3"
        value={tab}
        onChange={setTab}
        items={[
          { id: 'card', label: 'Карточка' },
          { id: 'doc', label: 'Справочник' },
        ]}
      />
      <div data-scroll-root className="scroll-thin min-h-0 flex-1 overflow-y-auto bg-bg px-5 py-4">
        {tab === 'doc' ? (
          <ServiceDocTab serviceId={id} />
        ) : r.error && !r.data ? (
          <ErrorBox error={r.error} onRetry={r.reload} />
        ) : !r.data || !s ? (
          <Loading />
        ) : (
          <div className="flex flex-col gap-4">
            {s.purpose ? <Markdown text={s.purpose} className="text-[13px]" /> : null}

            <dl className="divide-y divide-line rounded-card border border-line bg-surface px-3.5 py-1 shadow-card">
              <KV label="Репозиторий">
                {s.repo ? (
                  <a
                    href={`https://github.com/${s.repo}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex max-w-full items-center gap-1 font-mono text-[12px] text-accent hover:underline"
                  >
                    <span className="min-w-0 [overflow-wrap:anywhere]">{s.repo}</span>
                    <Icon name="external" size={12} className="shrink-0" />
                  </a>
                ) : (
                  <span className="text-subtle">не в git</span>
                )}
              </KV>
              <KV label="Сервер">{s.server ? <span className="font-mono text-[12px]">{s.server}</span> : '—'}</KV>
              <KV label="Домены">
                {s.domains.length ? (
                  <span className="flex flex-wrap gap-x-2 gap-y-0.5">
                    {s.domains.map((d) => (
                      <a
                        key={d}
                        href={/^https?:\/\//.test(d) ? d : `https://${d}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="min-w-0 font-mono text-[12px] text-accent [overflow-wrap:anywhere] hover:underline"
                      >
                        {d}
                      </a>
                    ))}
                  </span>
                ) : (
                  '—'
                )}
              </KV>
              <KV label="Стек">{[s.stack, s.runtime].filter(Boolean).join(' · ') || '—'}</KV>
              <KV label="Деплой">{s.deploy || '—'}</KV>
              <KV label="Базы данных">{s.databases.join(', ') || '—'}</KV>
              <KV label="Интеграции">{s.external_integrations.join(', ') || '—'}</KV>
              <KV label="Владельцы">{s.owners.join(', ') || '—'}</KV>
              {s.local_path ? <KV label="Локально">{<span className="font-mono text-[11.5px]">{s.local_path}</span>}</KV> : null}
            </dl>

            {s.critical_rules.length ? (
              <section className="rounded-card bg-danger-soft px-3.5 py-2.5">
                <h3 className="ct mb-1.5 flex items-center gap-1.5 !text-danger">
                  <Icon name="alert" size={13} /> Критичные правила
                </h3>
                <ul className="list-disc space-y-0.5 pl-4 text-[12.5px] break-words">
                  {s.critical_rules.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </section>
            ) : null}
            {s.change_risk_notes ? (
              <section className="rounded-card bg-warn-soft px-3.5 py-2.5">
                <h3 className="ct mb-1.5 !text-warn">Риски изменений</h3>
                <Markdown text={s.change_risk_notes} className="text-[12.5px]" />
              </section>
            ) : null}

            <DepList title="Зависит от" deps={r.data.upstream} field="to_service" onSelect={onSelect} />
            <DepList title="От него зависят" deps={r.data.downstream} field="from_service" onSelect={onSelect} />
            <ListBlock title="Контракты" items={s.contracts} />
            <ListBlock title="Ключевые сущности" items={s.key_entities} />
            <ListBlock title="Бизнес-процессы" items={s.business_processes} />

            <section>
              <h3 className="ct mb-1.5">Ответственные агенты</h3>
              {r.data.agents.length ? (
                <div className="flex flex-col gap-1">
                  {r.data.agents.map((a) => (
                    <AgentLabel key={a.id} id={a.id} size="sm" link />
                  ))}
                </div>
              ) : (
                <p className="text-[12.5px] text-subtle">Не назначены</p>
              )}
            </section>

            <section>
              <h3 className="ct mb-1.5">Кто сейчас меняет</h3>
              {r.data.intents.filter((i) => i.status === 'active').length ? (
                <ul className="flex flex-col gap-1.5">
                  {r.data.intents
                    .filter((i) => i.status === 'active')
                    .map((i) => (
                      <IntentRow key={i.id} it={i} showTask />
                    ))}
                </ul>
              ) : (
                <p className="text-[12.5px] text-subtle">Активных заявок нет</p>
              )}
            </section>

            {r.data.changes.length ? (
              <section>
                <h3 className="ct mb-1.5">Недавние изменения</h3>
                <ul className="flex flex-col gap-1">
                  {r.data.changes.slice(0, 12).map((c) => (
                    <li key={c.id} className="flex items-start gap-2 text-[12px]">
                      <Badge>{CHANGE_SOURCE_LABELS[c.source] ?? c.source}</Badge>
                      <span className="min-w-0 flex-1 break-words">
                        {c.url ? (
                          <a href={c.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            {c.title}
                          </a>
                        ) : (
                          c.title
                        )}
                        <span className="text-subtle"> · {c.author}</span>
                      </span>
                      <span className="tnum shrink-0 text-[11px] text-subtle" title={formatDateTime(c.happened_at)}>
                        {timeAgo(c.happened_at, now)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {r.data.tasks.length ? (
              <section>
                <h3 className="ct mb-1.5">Связанные задачи</h3>
                <ul className="flex flex-col">
                  {r.data.tasks.slice(0, 20).map((t) => (
                    <li key={t.id}>
                      <TaskLink id={t.id} className="flex min-w-0 items-center gap-2 rounded px-1 py-1 text-[12.5px] hover:bg-surface-2">
                        <StatusBadge status={t.status} />
                        <span className="tnum shrink-0 font-mono text-[11px] text-subtle">{t.key}</span>
                        <span className="min-w-0 flex-1 truncate">{t.title}</span>
                      </TaskLink>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {s.updated_at ? <p className="text-[11px] text-subtle">карточка обновлена {timeAgo(s.updated_at, now)}</p> : null}
          </div>
        )}
      </div>
    </div>
  )
}

/** Вкладка «Справочник» в карточке сервиса: обзор (раздел 0) и оглавление; разделы раскрываются на месте. */
function ServiceDocTab({ serviceId }: { serviceId: string }) {
  const r = useAsync((signal) => api.get<DocDetail>(`/api/docs/${encodeURIComponent(serviceId)}`, { signal }), [serviceId])
  const [open, setOpen] = useState<number | null>(null)
  useEffect(() => setOpen(null), [serviceId])

  if (r.error && !r.data) {
    return r.error instanceof ApiError && r.error.status === 404 ? (
      <Empty icon="book">Справочника пока нет</Empty>
    ) : (
      <ErrorBox error={r.error} onRetry={r.reload} />
    )
  }
  if (!r.data) return <Loading rows={5} />
  const { unit, sections } = r.data
  const parts = groupParts(sections)
  const top = parts[0]?.part === '' ? parts[0].sections : sections
  const overview = top.find((x) => /^0[.\s]|обзор/i.test(x.section)) ?? top[0]
  const rest = parts.some((p) => p.part) ? top.filter((x) => x !== overview && !/^части справочника/i.test(x.section)) : sections.filter((x) => x !== overview)
  const areaParts = parts.filter((p) => p.part)
  const title = (sections[0]?.title ?? unit).replace(/\s+—\s+справочник\s*$/i, '')

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 text-[13px] leading-snug font-semibold break-words text-fg-2">{title}</div>
        <Link to={docHref(unit)} className="inline-flex shrink-0 items-center gap-1 text-[12.5px] font-semibold text-accent hover:underline">
          Открыть целиком
          <Icon name="arrowRight" size={13} />
        </Link>
      </div>
      {overview ? (
        <section className="rounded-card border border-line bg-surface px-4 py-3.5 shadow-card">
          <h3 className="ct mb-2">{overview.section}</h3>
          <Markdown text={overview.body} className="doc text-[13.5px]" headingBase={2} />
        </section>
      ) : null}
      {areaParts.length ? (
        <section>
          <h3 className="ct mb-1.5">Части справочника</h3>
          <ul className="divide-y divide-line rounded-card border border-line bg-surface shadow-card">
            {areaParts.map((p) => {
              const ov = p.sections.find((x) => /^0[.\s]|обзор/i.test(x.section)) ?? p.sections[0]!
              const isOpen = open === ov.section_no
              return (
                <li key={p.part}>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpen((o) => (o === ov.section_no ? null : ov.section_no))}
                    className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-[13px] hover:bg-surface-2"
                  >
                    <Icon name="chevronRight" size={14} className={cx('shrink-0 text-subtle transition-transform', isOpen && 'rotate-90')} />
                    <span className={cx('min-w-0 flex-1 break-words', isOpen ? 'font-semibold text-fg' : 'text-fg-2')}>{p.title}</span>
                    <span className="tnum shrink-0 text-[11.5px] text-subtle">{formatChars(p.sections.reduce((n, x) => n + x.body.length, 0))}</span>
                  </button>
                  {isOpen ? (
                    <div className="border-t border-line px-4 py-3">
                      <Markdown text={ov.body} className="doc text-[13px]" headingBase={2} />
                      <Link to={docHref(unit, undefined, p.part)} className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-accent hover:underline">
                        Открыть часть в справочнике
                        <Icon name="arrowRight" size={13} />
                      </Link>
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </section>
      ) : null}
      {rest.length ? (
        <section>
          <h3 className="ct mb-1.5">{areaParts.length ? 'Разделы обзора' : 'Разделы'}</h3>
          <ul className="divide-y divide-line rounded-card border border-line bg-surface shadow-card">
            {rest.map((x) => {
              const isOpen = open === x.section_no
              return (
                <li key={x.id}>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpen((o) => (o === x.section_no ? null : x.section_no))}
                    className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-[13px] hover:bg-surface-2"
                  >
                    <Icon name="chevronRight" size={14} className={cx('shrink-0 text-subtle transition-transform', isOpen && 'rotate-90')} />
                    <span className={cx('min-w-0 flex-1 break-words', isOpen ? 'font-semibold text-fg' : 'text-fg-2')}>{x.section}</span>
                    <span className="tnum shrink-0 text-[11.5px] text-subtle">{formatChars(x.body.length)}</span>
                  </button>
                  {isOpen ? (
                    <div className="border-t border-line px-4 py-3">
                      <Markdown text={x.body} className="doc text-[13px]" headingBase={2} />
                      <Link to={docHref(unit, x.section_no)} className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-accent hover:underline">
                        Открыть раздел в справочнике
                        <Icon name="arrowRight" size={13} />
                      </Link>
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
